import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useLocation } from "react-router";
import { useGetAiPendingNotificationsQuery } from "../api/aiChatApi";
import { useHasExopenAiAccess } from "../hooks/useHasExopenAiAccess";
import { AiFeedbackSurveyDialog } from "./AiFeedbackSurveyDialog";
import { AiOptInReminderDialog } from "./AiOptInReminderDialog";

interface AiPendingNotificationsProps {
	companyDomainId: string;
}

function notificationKey(surveyKey: string, surveyVersion: number): string {
	return `${surveyKey}:${surveyVersion}`;
}

export function AiPendingNotifications({
	companyDomainId,
}: AiPendingNotificationsProps) {
	const hasAccess = useHasExopenAiAccess(companyDomainId);
	const { data } = useGetAiPendingNotificationsQuery(
		hasAccess ? companyDomainId : skipToken,
		{
			refetchOnMountOrArgChange: true,
		},
	);
	const [dismissedLocally, setDismissedLocally] = useState<Set<string>>(
		() => new Set(),
	);

	const dismissLocal = (key: string) => {
		setDismissedLocally((prev) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});
	};

	const { pathname } = useLocation();
	const isOnAiChat = pathname.startsWith(
		`/company-domains/${companyDomainId}/ai-chat`,
	);

	const survey = data?.notifications.find((n) => n.kind === "survey");
	const reminder = !isOnAiChat
		? data?.notifications.find((n) => n.kind === "optin-reminder")
		: undefined;

	return (
		<>
			{survey &&
				(() => {
					const key = notificationKey(survey.surveyKey, survey.surveyVersion);
					if (dismissedLocally.has(key)) return null;
					return (
						<AiFeedbackSurveyDialog
							open
							companyDomainId={companyDomainId}
							surveyKey={survey.surveyKey}
							surveyVersion={survey.surveyVersion}
							questions={survey.questions}
							onClose={() => dismissLocal(key)}
						/>
					);
				})()}
			{reminder &&
				(() => {
					const key = notificationKey(
						reminder.surveyKey,
						reminder.surveyVersion,
					);
					if (dismissedLocally.has(key)) return null;
					return (
						<AiOptInReminderDialog
							companyDomainId={companyDomainId}
							surveyKey={reminder.surveyKey}
							surveyVersion={reminder.surveyVersion}
							onDismiss={() => dismissLocal(key)}
						/>
					);
				})()}
		</>
	);
}
